import React, { useCallback, useContext, useRef, useState } from "react";
import { LinkOutlined } from "@ant-design/icons";
import { Button, Layout, Modal, Space, Typography } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { BannerModal } from "@/Components/Common/BannerModal";
import CommonForm from "@/Components/Common/CommonForm";
import CommonTable from "@/Components/Common/CommonTable/CommonTable";
import TruncatedText from "@/Components/Common/TruncatedText";
import { ToastContext } from "@/Context/ToastContext";
import { getValue } from "@/Utils/Index";
import {
  activeOrganizationPlanformFields,
  activeOrganizationPlaninitialValues,
} from "../../../Assets/Mocks/ActivePlan.mock";
import CountryData from "../../../Assets/Mocks/countries.json";

const { Text } = Typography;

function Organization() {
  const [organizationData, setOrganizationData] = useState();
  const formRef = useRef();
  const [isActivePlanOpen, setIsActivePlanOpen] = useState(false);
  const childRef = useRef();
  const [planFormValues, setPlanFormValues] = useState(activeOrganizationPlaninitialValues);
  const fetch = useAuthenticatedFetch();
  const { showToast } = useContext(ToastContext);
  const [isCancelPlanModalOpen, setIsCancelPlanModalOpen] = useState(false);
  const [isMoveJobOpen, setIsMoveJobOpen] = useState(false);

  const formatTimeAgo = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return "N/A";
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      let data = {
        activePlan: e,
        pricing: organizationData,
      };
      if (e.orgId) {
        const res = await fetch.post("/admin/moveJob", e);
        childRef.current.fetchData();
        showToast("Move Job successfully");
        setIsMoveJobOpen(false);
      } else {
        const pricingPlanRes = await fetch.put("/admin/updatePlan", data);
        childRef.current.fetchData();
        showToast("Plan Update successfully");

        setIsActivePlanOpen(false);
      }
    },
    [childRef, organizationData]
  );

  const handleCanceledPlan = useCallback(async () => {
    const pricingPlanRes = await fetch.put("/admin/cancelPlan", organizationData);
    childRef.current.fetchData();
    showToast("Cancel Plan successfully");

    setIsCancelPlanModalOpen(false);
  }, [childRef, organizationData]);

  const handleCancelPlan = useCallback(
    async (row) => {
      setOrganizationData(row);
      setIsCancelPlanModalOpen(true);
    },
    [organizationData]
  );

  const handleActivePlan = useCallback(
    async (row) => {
      setOrganizationData(row);
      setIsActivePlanOpen(true);
    },
    [organizationData]
  );

  const handleMoveJob = useCallback(async () => {
    setIsMoveJobOpen(true);
  }, [organizationData]);

  const handleMoveJobModalClose = () => {
    setIsMoveJobOpen(false);
  };

  const handleModalClose = () => {
    setIsActivePlanOpen(false);
  };

  const handleCancelPlanCanceled = () => {
    setIsCancelPlanModalOpen(false);
  };

  const addPlan = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const moveJob = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleRedirect = useCallback((url) => {
    window.open(url, "_blank");
  }, []);

  function getItemNameByCountryCode(countryCode) {
    const country = CountryData[countryCode];
    return country ? country.itemName : null;
  }

  const ArchivedData = useCallback((rows) => {
    if (rows?.length <= 0) return [];
    return rows?.map((row, index) => {
      return {
        key: row._id,
        no: index + 1,
        name: (
          <Space direction="vertical" size="middle">
            <Text strong style={{ fontSize: "16px" }}>
              {row?.orgId?.name}
            </Text>
            <Space size="middle">
              <Text type="secondary">Domain :</Text>
              <Text strong>{row?.orgId?.domain}</Text>
            </Space>
            <Space size="middle">
              <Text type="secondary">Country :</Text>
              <Text strong>{getItemNameByCountryCode(row?.orgId?.country)}</Text>
            </Space>
            {row?.orgId?.companySize && (
              <Space size="middle">
                <Text type="secondary">Company Size :</Text>
                <Text strong>{row?.orgId?.companySize}</Text>
              </Space>
            )}
            {row?.orgId?.website && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text type="secondary">Website :</Text>
                <TruncatedText text={row?.orgId?.website} maxLength={25} />
                <Button
                  type="text"
                  icon={<LinkOutlined />}
                  size="small"
                  onClick={() => handleRedirect(row?.orgId?.website)}
                />
              </div>
            )}
            {row?.orgId?.linkedInProfile && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Text type="secondary">LinkedIn Profile :</Text>
                <TruncatedText text={row?.orgId?.linkedInProfile} maxLength={25} />
                <Button
                  type="text"
                  icon={<LinkOutlined />}
                  size="small"
                  onClick={() => handleRedirect(row?.orgId?.linkedInProfile)}
                />
              </div>
            )}
            {row?.orgId?.userFoundUs && (
              <Space size="middle">
                <Text type="secondary">Found Us :</Text>
                <Text strong>{row?.orgId?.userFoundUs}</Text>
              </Space>
            )}
          </Space>
        ),
        plan: (
          <Space direction="vertical" size="middle">
            <Space size="middle">
              <Text type="secondary">Plan Name :</Text>
              <Text strong>{row?.planType ? row?.planType : "Free"}</Text>
            </Space>
            <Space size="middle">
              <Text type="secondary">Plan Price :</Text>
              <Text strong>{row?.originalPrice}</Text>
            </Space>
            <Space size="middle">
              <Text type="secondary">Trial Days :</Text>
              <Text strong>{row?.isTrial == true ? "Yes" : "No"}</Text>
            </Space>
          </Space>
        ),
        features: (
          <Space direction="vertical" size="middle">
            <Space size="middle">
              <Text type="secondary">Job Credit :</Text>
              <Text strong>
                {row?.featureLimitation?.jobCredit ? row?.featureLimitation?.jobCredit : "Unlimited"}
              </Text>
            </Space>
            <Space size="middle">
              <Text type="secondary">User Credit :</Text>
              <Text strong>
                {row?.featureLimitation?.userCredit ? row?.featureLimitation?.userCredit : "Unlimited"}
              </Text>
            </Space>
            {row?.featureLimitation?.jobExpired && (
              <Space size="middle">
                <Text type="secondary">Job Expired :</Text>
                <Text strong>{row?.featureLimitation?.jobExpired}</Text>
              </Space>
            )}
          </Space>
        ),
        created: (
          <Space direction="vertical" size="middle">
            <Space size="middle">
              <Text> {formatTimeAgo(row?.orgId?.created)}</Text>
            </Space>
          </Space>
        ),
        action: (
          <Space direction="vertical" size="middle">
            {row?.planId == "Free" ? (
              <Button onClick={() => handleActivePlan(row)}>Active Plan</Button>
            ) : (
              <Button onClick={() => handleCancelPlan(row)}>Cancel Plan</Button>
            )}
          </Space>
        ),
      };
    });
  }, []);

  const columns = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
    },
    {
      title: "Features",
      dataIndex: "features",
      key: "features",
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
    },
  ];

  return (
    <Layout.Content style={{ padding: "0", width: "100%" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "flex-end" }} size="middle">
          <Button onClick={() => handleMoveJob()}>Move Job</Button>
        </Space>
        <CommonTable
          resourceName={{
            singular: "Organization",
            plural: "Organizations",
          }}
          title="Organizations"
          queryPlaceholder="Search User by (Name)"
          url={`admin/organization`}
          selectable={false}
          rowsData={ArchivedData}
          isFilterVisible
          columns={columns}
          searchKey={["name"]}
          ref={childRef}
          isAdd={false}
          verticalAlign="center"
        />
      </Space>
      <BannerModal
        open={isActivePlanOpen}
        onClose={handleModalClose}
        title="Add New Job"
        primaryAction={{
          content: "Submit",
          onAction: addPlan,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: handleModalClose,
          },
        ]}
        children={
          <CommonForm
            onSubmit={handleSubmit}
            formRef={formRef}
            initialValues={planFormValues}
            formFields={activeOrganizationPlanformFields}
            isSave={false}
          />
        }
        size={"large"}
      />

      <Modal
        open={isCancelPlanModalOpen}
        onCancel={handleCancelPlanCanceled}
        title="Confirm Cancel Plan"
        footer={[
          <Button key="cancel" onClick={handleCancelPlanCanceled}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleCanceledPlan}>
            Cancel Plan
          </Button>,
        ]}
      >
        <Text>Are you sure you want to Cancel this Plan?</Text>
      </Modal>

      <BannerModal
        open={isMoveJobOpen}
        onClose={handleMoveJobModalClose}
        title="Move Job"
        primaryAction={{
          content: "Submit",
          onAction: moveJob,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: handleMoveJobModalClose,
          },
        ]}
        children={
          <CommonForm
            onSubmit={handleSubmit}
            formRef={formRef}
            // initialValues={moveJobinitialValues}
            // formFields={moveJobFields}
            isSave={false}
          />
        }
      />
    </Layout.Content>
  );
}

export default Organization;
